import { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import {
  AlertDialog, AlertDialogTrigger, AlertDialogContent, AlertDialogHeader,
  AlertDialogTitle, AlertDialogDescription, AlertDialogFooter,
  AlertDialogCancel, AlertDialogAction,
} from '../ui/alert-dialog';
import {
  HardDrive, FloppyDisk, ArrowCounterClockwise, Trash, DownloadSimple, Clock, ShieldCheck,
} from '@phosphor-icons/react';
import { API } from './adminConstants';

const formatBytes = (bytes) => {
  if (!bytes) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB'];
  let i = 0; let n = bytes;
  while (n >= 1024 && i < units.length - 1) { n /= 1024; i++; }
  return `${n.toFixed(n < 10 && i > 0 ? 1 : 0)} ${units[i]}`;
};

const formatDate = (iso) => {
  try { return new Date(iso).toLocaleString('en-GB', { dateStyle: 'medium', timeStyle: 'short' }); }
  catch { return iso; }
};

export function Backups() {
  const { getAuthHeader } = useAuth();
  const [backups, setBackups] = useState([]);
  const [retention, setRetention] = useState(14);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [manualLabel, setManualLabel] = useState('');
  const [jobProgress, setJobProgress] = useState(null);

  const load = async () => {
    try {
      const r = await axios.get(`${API}/admin/backups/list`, { headers: getAuthHeader() });
      setBackups(r.data.backups || []);
      setRetention(r.data.auto_retention || 14);
    } catch {
      toast.error('Failed to load backups');
    }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  // Poll a background job until done; returns the final job object or
  // throws. Surfaces both backend `detail` and HTTP status in errors.
  const pollJob = async (jobId, onProgress) => {
    for (let i = 0; i < 300; i += 1) {  // 300 * 2s = 10 min
      await new Promise(res => setTimeout(res, 2000));
      let j;
      try {
        const r = await axios.get(`${API}/admin/data/jobs/${jobId}`, { headers: getAuthHeader() });
        j = r.data;
      } catch {
        continue;  // transient poll failures are fine, just retry
      }
      if (onProgress) onProgress(j);
      if (j.status === 'done') return j;
      if (j.status === 'error') throw new Error(j.error || 'Job failed');
    }
    throw new Error('Still running after 10 minutes — please refresh');
  };

  const createBackup = async () => {
    setCreating(true);
    setJobProgress({ stage: 'starting…', progress: 0 });
    try {
      const q = manualLabel ? `?label=${encodeURIComponent(manualLabel)}` : '';
      const start = await axios.post(`${API}/admin/backups/create${q}`, null, { headers: getAuthHeader() });
      const j = await pollJob(start.data.job_id, setJobProgress);
      const meta = j.result?.backup;
      toast.success(`Backup created — ${meta ? formatBytes(meta.size_bytes) : 'ok'}`);
      setManualLabel('');
      await load();
    } catch (e) {
      const detail = e.response?.data?.detail
        || (e.response ? `HTTP ${e.response.status} ${e.response.statusText || ''}` : null)
        || e.message
        || 'Backup failed';
      toast.error(`Backup failed: ${detail}`);
    }
    setCreating(false);
    setJobProgress(null);
  };

  const downloadBackup = async (b) => {
    setBusyId(b.id);
    try {
      const r = await axios.get(`${API}/admin/backups/${b.id}/download`, {
        headers: getAuthHeader(),
        responseType: 'blob',
      });
      const blob = new Blob([r.data], { type: 'application/json' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = b.filename;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Backup downloaded');
    } catch (e) {
      const detail = e.response?.data?.detail
        || (e.response ? `HTTP ${e.response.status}` : null)
        || e.message
        || 'Download failed';
      toast.error(`Download failed: ${detail}`);
    }
    setBusyId(null);
  };

  const restoreBackup = async (b, mode) => {
    setBusyId(b.id);
    setJobProgress({ stage: 'starting…', progress: 0 });
    try {
      const start = await axios.post(
        `${API}/admin/backups/${b.id}/restore?mode=${mode}`,
        null,
        { headers: getAuthHeader() },
      );
      const j = await pollJob(start.data.job_id, setJobProgress);
      toast.success(`Restored (${mode}) from ${j.result?.from || b.filename}`);
      await load();
    } catch (e) {
      const detail = e.response?.data?.detail
        || (e.response ? `HTTP ${e.response.status}` : null)
        || e.message
        || 'Restore failed';
      toast.error(`Restore failed: ${detail}`);
    }
    setBusyId(null);
    setJobProgress(null);
  };

  const deleteBackup = async (b) => {
    setBusyId(b.id);
    try {
      await axios.delete(`${API}/admin/backups/${b.id}`, { headers: getAuthHeader() });
      toast.success('Backup deleted');
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Delete failed');
    }
    setBusyId(null);
  };

  if (loading) return <div className="py-12 text-center"><div className="animate-spin rounded-full h-8 w-8 border-2 border-lime-600 border-t-transparent mx-auto"></div></div>;

  const autoCount = backups.filter(b => b.type === 'auto').length;
  const manualCount = backups.filter(b => b.type === 'manual').length;

  return (
    <div data-testid="backups-panel">
      <div className="mb-6">
        <h1 className="font-outfit font-bold text-2xl text-slate-900 flex items-center gap-2">
          <HardDrive size={26} weight="duotone" className="text-lime-600" />
          Backups
        </h1>
        <p className="text-slate-500 text-sm">
          Snapshots of all CMS content stored safely in the database. Automatic nightly backups run at <strong>02:00 UTC</strong>; you can also take one manually any time.
        </p>
      </div>

      {/* Manual backup */}
      <div className="bg-gradient-to-br from-lime-50 to-white rounded-xl border border-lime-200 p-6 mb-6">
        <div className="flex items-start gap-4 flex-wrap">
          <div className="p-3 rounded-xl bg-lime-600 text-white shrink-0">
            <FloppyDisk size={24} weight="duotone" />
          </div>
          <div className="flex-1 min-w-[260px]">
            <h3 className="font-semibold text-slate-900 mb-1">Take a backup now</h3>
            <p className="text-sm text-slate-700 mb-3">Captures every page, news article, case study, image and PDF in the current environment. Manual backups are kept until you delete them.</p>
            <input
              type="text"
              value={manualLabel}
              onChange={(e) => setManualLabel(e.target.value)}
              placeholder="Optional label (e.g. before adding May news)"
              className="w-full sm:max-w-sm px-3 py-2 text-sm rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-lime-500"
              data-testid="backup-label-input"
            />
          </div>
          <Button
            onClick={createBackup}
            disabled={creating}
            className="bg-lime-600 hover:bg-lime-700 shrink-0"
            data-testid="create-backup-btn"
          >
            <FloppyDisk size={15} className="mr-1" />
            {creating ? 'Saving…' : 'Create backup'}
          </Button>
        </div>
        {jobProgress && creating && (
          <div className="mt-3 p-2 rounded bg-white border border-lime-200 text-xs" data-testid="backup-progress">
            <div className="flex justify-between mb-1">
              <span className="text-slate-700">{jobProgress.stage || jobProgress.status || 'working…'}</span>
              <span className="text-slate-500 font-mono">{jobProgress.progress ?? 0}%</span>
            </div>
            <div className="h-1.5 w-full bg-slate-200 rounded-full overflow-hidden">
              <div className="h-full bg-lime-500 transition-all duration-500" style={{ width: `${jobProgress.progress ?? 0}%` }}></div>
            </div>
          </div>
        )}
      </div>

      {/* Retention/scheduler info */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-6">
        <div className="bg-white rounded-xl border border-slate-200 p-4 flex items-center gap-3">
          <div className="p-2 rounded-lg bg-slate-100 text-slate-700"><Clock size={20} weight="duotone" /></div>
          <div>
            <p className="text-xs text-slate-500 uppercase tracking-wide">Nightly schedule</p>
            <p className="text-sm font-semibold text-slate-900">02:00 UTC daily</p>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-4 flex items-center gap-3">
          <div className="p-2 rounded-lg bg-amber-100 text-amber-700"><ShieldCheck size={20} weight="duotone" /></div>
          <div>
            <p className="text-xs text-slate-500 uppercase tracking-wide">Auto retention</p>
            <p className="text-sm font-semibold text-slate-900">last {retention} kept · {autoCount} stored</p>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-4 flex items-center gap-3">
          <div className="p-2 rounded-lg bg-lime-100 text-lime-700"><HardDrive size={20} weight="duotone" /></div>
          <div>
            <p className="text-xs text-slate-500 uppercase tracking-wide">Manual backups</p>
            <p className="text-sm font-semibold text-slate-900">{manualCount} stored</p>
          </div>
        </div>
      </div>

      {/* Backup list */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between">
          <h3 className="font-semibold text-slate-900">All backups</h3>
          <span className="text-xs text-slate-500">{backups.length} total</span>
        </div>
        {backups.length === 0 ? (
          <div className="p-10 text-center text-sm text-slate-500" data-testid="backups-empty">
            No backups yet. The first nightly backup will run at 02:00 UTC, or take one manually above.
          </div>
        ) : (
          <ul className="divide-y divide-slate-100">
            {backups.map(b => (
              <li key={b.id} className="px-5 py-4 flex flex-wrap items-center gap-3" data-testid={`backup-row-${b.id}`}>
                <div className="flex-1 min-w-[260px]">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wide ${b.type === 'auto' ? 'bg-slate-100 text-slate-700' : 'bg-lime-100 text-lime-700'}`}>
                      {b.type}
                    </span>
                    <span className="text-sm font-medium text-slate-900">{formatDate(b.created_at)}</span>
                    {b.label && <span className="text-xs text-slate-500 italic truncate max-w-[260px]">“{b.label}”</span>}
                  </div>
                  <p className="text-xs text-slate-500 mt-1 font-mono truncate">{b.filename}</p>
                  <p className="text-xs text-slate-500 mt-0.5">{b.total_records} records · {formatBytes(b.size_bytes)}</p>
                </div>
                <div className="flex gap-1.5 flex-wrap shrink-0">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => downloadBackup(b)}
                    disabled={busyId === b.id}
                    className="border-slate-300 text-slate-700 hover:bg-slate-50"
                    data-testid={`backup-download-${b.id}`}
                  >
                    <DownloadSimple size={14} className="mr-1" /> Download
                  </Button>

                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button
                        size="sm"
                        disabled={busyId === b.id}
                        className="bg-amber-600 hover:bg-amber-700 text-white"
                        data-testid={`backup-restore-${b.id}`}
                      >
                        <ArrowCounterClockwise size={14} className="mr-1" /> Restore
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Restore from this backup?</AlertDialogTitle>
                        <AlertDialogDescription asChild>
                          <div className="text-sm text-slate-600 space-y-2">
                            <p>Restoring will replace the content in this environment with the snapshot taken on <strong>{formatDate(b.created_at)}</strong>.</p>
                            <p>A safety backup of the current state will be taken automatically before any overwrite, so this is reversible.</p>
                            <p>Choose a mode:</p>
                            <ul className="text-xs list-disc pl-5">
                              <li><strong>Overwrite</strong> — wipes current collections, then loads the backup (clean mirror).</li>
                              <li><strong>Merge</strong> — only fills in missing records, leaves the rest untouched.</li>
                            </ul>
                          </div>
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter className="flex flex-wrap gap-2">
                        <AlertDialogCancel data-testid={`backup-restore-cancel-${b.id}`}>Cancel</AlertDialogCancel>
                        <AlertDialogAction
                          onClick={() => restoreBackup(b, 'merge')}
                          className="bg-lime-600 hover:bg-lime-700"
                          data-testid={`backup-restore-merge-${b.id}`}
                        >
                          Restore (Merge)
                        </AlertDialogAction>
                        <AlertDialogAction
                          onClick={() => restoreBackup(b, 'overwrite')}
                          className="bg-red-600 hover:bg-red-700"
                          data-testid={`backup-restore-overwrite-${b.id}`}
                        >
                          Restore (Overwrite)
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>

                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={busyId === b.id}
                        className="border-red-200 text-red-600 hover:bg-red-50"
                        data-testid={`backup-delete-${b.id}`}
                      >
                        <Trash size={14} />
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Delete this backup?</AlertDialogTitle>
                        <AlertDialogDescription>
                          {`The backup file from ${formatDate(b.created_at)} (${formatBytes(b.size_bytes)}) will be permanently removed. This action cannot be undone.`}
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction
                          onClick={() => deleteBackup(b)}
                          className="bg-red-600 hover:bg-red-700"
                          data-testid={`backup-delete-confirm-${b.id}`}
                        >
                          Delete backup
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="mt-6 p-4 bg-slate-50 border border-slate-200 rounded-xl text-xs text-slate-600 space-y-1">
        <p><strong>How it works:</strong> Backups are stored inside the database itself (GridFS), so they persist through redeploys and container restarts.</p>
        <p>Run a manual backup before any large content edits — it only takes a few seconds.</p>
      </div>
    </div>
  );
}
